import type { QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

type Meta = {
  continuation?: {
    first?: string;
    last?: string;
  };
  pagination?: {
    page: number;
    total: number;
  };
};

/**
 * @deprecated Will be replaced by ts-rest's useInfiniteQuery post-hydra merge.
 *
 * This hook can be used to paginate a static list of values.
 * It can be passed directly to a Paginator component, like so:
 *
 * ```
 * const query = usePaginatedQuery({
 *   queryFn: ({ meta }) => {
 *     return fetch('/api/destinations', { params: meta.pagination });)
 *   }
 * });
 *
 * return (
 *   <Pagination {...query.pagination} />
 * );
 * ```
 */

const DEFAULT_PAGE_SIZE = 10;

type Query<T> = UseQueryResult<T & { meta: Meta }>;
type QueryOptions<T> = UseQueryOptions<T & { meta: Meta }>;
type ExtendedQueryResult<T> = Query<T> & {
  pagination: PaginationResult;
};

export type PaginationRequest = {
  limit?: number;
  page?: number;
};
export type PaginationResult = {
  page: number;
  pageSize: number;
  totalItemCount: number;
  onPageChange: (page: number) => void;
  reset: (page?: number) => void;
};

type PaginationOptions = {
  initialPage?: number;
  pageSize?: number;
  /**
   * Controlled page. When provided, this value (not the hook's internal state) is the single
   * source of truth for the requested page. Pair with `onPageChange` so page changes driven
   * externally (e.g. URL state) take effect on the same render instead of lagging by an effect.
   */
  page?: number;
  onPageChange?: (page: number) => void;
};

export function usePaginatedQuery<T>(
  options: QueryOptions<T>,
  { initialPage = 1, pageSize = DEFAULT_PAGE_SIZE, page: controlledPage, onPageChange }: PaginationOptions = {},
) {
  const [internalPage, setInternalPage] = useState(initialPage);
  const isControlled = controlledPage != null;
  const page = isControlled ? controlledPage : internalPage;
  const setPage = isControlled ? (onPageChange ?? (() => {})) : setInternalPage;
  const pagination = useRef({ page, pageSize, totalItemCount: 0 });

  // Update the paginator as soon as the page changes. (ie: the user requested another page)
  useEffect(() => {
    pagination.current.page = page;
  }, [page]);

  const query = useQuery({
    ...options,
    meta: {
      pagination: {
        limit: pageSize,
        page,
      },
    },
    queryKey: [
      ...(options.queryKey ?? []),
      {
        limit: pageSize,
        page,
      },
    ] as QueryKey,
    placeholderData: keepPreviousData,
  });

  // Update the paginator when the query returns new results.
  if (query.data?.meta?.pagination != null) {
    pagination.current = {
      page: query.data.meta.pagination.page,
      totalItemCount: query.data.meta.pagination.total,
      pageSize,
    };
  }

  const extendedQuery = query as ExtendedQueryResult<T>;

  // Add pagination to the query, so it can be used with the Paginator component.
  extendedQuery.pagination = {
    ...pagination.current,
    // In controlled mode the controlled value is authoritative for the displayed page, so it can
    // never lag behind the server echo (`keepPreviousData`) or a stale internal state.
    page: isControlled ? page : pagination.current.page,
    onPageChange: setPage,
    reset: (nextPage = 1) => setPage(nextPage),
  };

  return extendedQuery;
}
